import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useErrorHandler } from '../../services/errorHandler';
import { useForm } from '../../hooks/useForm';
import { authSchemas } from '../../utils/validationSchemas';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import ModernLanguageSwitcher from '../../components/common/ModernLanguageSwitcher';
import { EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const { register } = useAuth();
  const navigate = useNavigate();
  const { isRTL } = useLanguage();
  const errorHandler = useErrorHandler();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: authSchemas(t).register,
    onSubmit: async (values) => {
      const result = await register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone || undefined,
        password: values.password,
      });

      if (result.success) {
        errorHandler.showSuccess(t('success.register', 'Registration successful!'));
        setSuccess(true);
      } else {
        // Handle validation errors from backend
        if (result.errors && result.errors.length > 0) {
          // Build localized error descriptions
          const errorDescriptions: string[] = [];
          
          result.errors.forEach((error: any) => {
            // Set field errors for specific fields if applicable
            if (error.field) {
              form.setFieldError(error.field, error.description);
            }
            
            // Get localized description based on error code
            let localizedDesc = error.description; // Default to original description
            const errorCode = error.code || '';
            
            if (errorCode === 'DuplicateUserName' || errorCode === 'DuplicateEmail') {
              // Extract email/username from description if available
              const emailMatch = error.description?.match(/'([^']+)'/);
              const email = emailMatch ? emailMatch[1] : '';
              
              if (errorCode === 'DuplicateEmail') {
                localizedDesc = email 
                  ? t('auth.duplicateEmail', { defaultValue: `Email '${email}' is already taken.`, email })
                  : t('auth.emailAlreadyExists', 'This email is already registered.');
              } else if (errorCode === 'DuplicateUserName') {
                localizedDesc = email
                  ? t('auth.duplicateUserName', { defaultValue: `Username '${email}' is already taken.`, email })
                  : t('auth.usernameAlreadyExists', 'This username is already taken.');
              }
            } else if (errorCode === 'PasswordTooShort') {
              localizedDesc = t('auth.passwordTooShort', 'Password must be at least 6 characters long');
            } else if (errorCode === 'PasswordRequiresNonAlphanumeric') {
              localizedDesc = t('auth.passwordRequiresSpecial', 'Password must contain at least one special character');
            } else if (errorCode === 'PasswordRequiresDigit') {
              localizedDesc = t('auth.passwordRequiresDigit', 'Password must contain at least one digit');
            } else if (errorCode === 'PasswordRequiresUpper') {
              localizedDesc = t('auth.passwordRequiresUpper', 'Password must contain at least one uppercase letter');
            } else if (errorCode === 'PasswordRequiresLower') {
              localizedDesc = t('auth.passwordRequiresLower', 'Password must contain at least one lowercase letter');
            }
            
            if (localizedDesc && !error.field) {
              errorDescriptions.push(localizedDesc);
            }
          });
          
          // Show main error message with appended descriptions
          const mainMessage = t('auth.registerFailed', 'Registration failed. Please try again.');
          const fullMessage = errorDescriptions.length > 0
            ? `${mainMessage} ${errorDescriptions.join(' ')}`
            : mainMessage;
          
          errorHandler.showWarning(fullMessage);
        } else {
          errorHandler.showWarning(result.message || t('auth.registerFailed', 'Registration failed. Please try again.'));
        }
      }
    }
  });

  return (
    <div className="w-full max-w-md mx-auto" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Back to Home Button */}
      <div className="mb-6 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-gray-600 hover:text-primary-600 transition-colors duration-200"
        >
          <ArrowLeftIcon className={`h-4 w-4 ${isRTL ? 'ml-1' : 'mr-1'}`} />
          {t('common.back', 'Back')} {t('nav.home', 'Home')}
        </Link>
        <div className="hidden sm:block">
          <ModernLanguageSwitcher />
        </div>
      </div>

      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <div className="h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">RF</span>
          </div>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
          {t('auth.register')}
        </h2>
        <p className="mt-2 text-xs sm:text-sm text-gray-600">
          {t('auth.registerSubtitle', 'Create your account to get started')}
        </p>
      </div>

      {!success ? (
        <>
        <form onSubmit={form.handleSubmit} className="space-y-5 sm:space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Input
              {...form.getFieldProps('firstName')}
              label={t('auth.firstName')}
              type="text"
              autoComplete="given-name"
              placeholder={t('auth.firstName', 'First Name')}
            />
            <Input
              {...form.getFieldProps('lastName')}
              label={t('auth.lastName')}
              type="text"
              autoComplete="family-name"
              placeholder={t('auth.lastName', 'Last Name')}
            />
          </div>

          <Input
            {...form.getFieldProps('email')}
            label={t('auth.email')}
            type="email"
            autoComplete="email"
            placeholder={t('auth.email', 'Email')}
          />

          <Input
            {...form.getFieldProps('phone')}
            label={t('auth.phone', 'Phone Number')}
            type="tel"
            autoComplete="tel"
            placeholder={t('auth.phone', 'Phone Number')}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.password')}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                {...form.getFieldProps('password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('auth.password', 'Password')}
                className={`w-full px-3 py-2 ${isRTL ? 'pl-10 pr-3' : 'pr-10'} border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${
                  form.errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50' : 'hover:border-gray-400'
                }`}
              />
              <button
                type="button"
                className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {form.errors.password && (
              <p className="text-sm text-red-600 font-medium flex items-center gap-1 mt-1">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {form.errors.password}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.confirmPassword')}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <input
                {...form.getFieldProps('confirmPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('auth.confirmPassword', 'Confirm Password')}
                className={`w-full px-3 py-2 ${isRTL ? 'pl-10 pr-3' : 'pr-10'} border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${
                  form.errors.confirmPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500 bg-red-50' : 'hover:border-gray-400'
                }`}
              />
              <button
                type="button"
                className={`absolute inset-y-0 ${isRTL ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center`}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
            {form.errors.confirmPassword && (
              <p className="text-sm text-red-600 font-medium flex items-center gap-1 mt-1">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {form.errors.confirmPassword}
              </p>
            )}
          </div>

          <div className="flex items-center">
            <input
              id="agree-terms"
              name="agree-terms"
              type="checkbox"
              required
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="agree-terms" className={`${isRTL ? 'mr-2' : 'ml-2'} block text-sm text-gray-900`}>
              {t('auth.agreeTo', 'I agree to the')} <Link to="/terms" className="text-primary-600 hover:text-primary-500">{t('auth.terms', 'Terms of Service')}</Link> {t('auth.and', 'and')} <Link to="/privacy" className="text-primary-600 hover:text-primary-500">{t('auth.privacy', 'Privacy Policy')}</Link>
            </label>
          </div>

          <Button
            type="submit"
            loading={form.isSubmitting}
            className="w-full"
          >
            {t('auth.signUp')}
          </Button>
        </form>
        </>
      ) : (
        <div className="space-y-4 text-center">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 p-4">
            {t('auth.registerSuccessCheckEmail', 'We sent you a confirmation email. Please verify your email to sign in.')}
          </div>
          <p className="text-sm text-gray-600">{t('auth.afterConfirmLogin', 'After confirming, please return to the login page.')}</p>
          <Link to="/login" className="inline-block"><Button>{t('auth.goToLogin', 'Go to Login')}</Button></Link>
        </div>
      )}

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">
              {t('auth.hasAccount', 'Already have an account?')}
            </span>
          </div>
        </div>

        <div className="mt-6">
          <Link
            to="/login"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary-600 hover:bg-secondary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500"
          >
            {t('auth.signIn')}
          </Link>
        </div>

        <div className="mt-4 sm:hidden flex justify-center">
          <ModernLanguageSwitcher />
        </div>
      </div>
    </div>
  );
};

export default Register;
